<template>
  <page :title="title" :breadcrumb="breadcrumb">
    <div>
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="tranMode === 'New'">mdi-plus</v-icon>
          <v-icon v-if="tranMode === 'Review'">mdi-pencil</v-icon>
          <v-icon v-if="tranMode === 'Query'">mdi-magnify</v-icon>
          <v-toolbar-title v-if="tranMode === 'New'">New Accounting Period</v-toolbar-title>
          <v-toolbar-title v-if="tranMode === 'Review'">Edit Accounting Period</v-toolbar-title>
          <v-toolbar-title v-if="tranMode === 'Query'">View Accounting Period</v-toolbar-title>
        </v-toolbar>
        <v-divider></v-divider>
        <v-card-text>
          <message :messages="messages"></message>
          <form>
            <v-container fluid grid-list-lg>
              <v-layout row wrap>
                <v-flex xs6 v-if="tranMode === 'New'">
                  <v-autocomplete
                    v-model="acctPeriodForm.modNo"
                    :label="labels.modNo"
                    :items="modNoList"
                    item-text="label"
                    data-vv-scope="filter"
                    item-value="value"
                    :error-messages="errors.collect('modNo')"
                    data-vv-name="modNo"
                    :data-vv-as="labels.modNo"
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 v-else>
                  <v-subheader class="queryLabel">{{ labels.modNo }}</v-subheader>
                  <div class="queryValue">{{ acctPeriodForm.modNoLbl }}</div>
                </v-flex>
                <v-flex xs6 v-if="tranMode === 'New'">
                  <cal-picker
                    :label="labels.acctPerEndDate"
                    v-model="acctPeriodForm.acctPerEndDate"
                    :error-messages="errors.collect('acctPerEndDate')"
                    data-vv-name="acctPerEndDate"
                    :data-vv-as="labels.acctPerEndDate"
                    v-validate="'required|isPastYear'"
                  ></cal-picker>
                </v-flex>
                <v-flex xs6 v-else>
                  <v-subheader class="queryLabel">{{ labels.acctPerEndDate }}</v-subheader>
                  <div class="queryValue">{{ formatDisplayDate(acctPeriodForm.acctPerEndDate) }}</div>
                </v-flex>
                <v-flex xs6 v-if="tranMode === 'Query'">
                  <v-subheader class="queryLabel">{{ labels.acctPerMth }}</v-subheader>
                  <div class="queryValue">{{ acctPeriodForm.acctPerMth }}</div>
                </v-flex>
                <v-flex xs6 v-else>
                  <v-text-field
                    v-model="acctPeriodForm.acctPerMth"
                    :label="labels.acctPerMth"
                    :error-messages="errors.collect('acctPerMth')"
                    data-vv-name="acctPerMth"
                    :data-vv-as="labels.acctPerMth"
                    type="number"
                    v-validate="'required|cannotBeZero|acctPerMthRange'"
                    :min="0"
                    :max="13"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 v-if="tranMode === 'Query'">
                  <v-subheader class="queryLabel">{{ labels.acctPerYr }}</v-subheader>
                  <div class="queryValue">{{ acctPeriodForm.acctPerYr }}</div>
                </v-flex>
                <v-flex xs6 v-else>
                  <v-text-field
                    v-model="acctPeriodForm.acctPerYr"
                    :label="labels.acctPerYr"
                    :error-messages="errors.collect('acctPerYr')"
                    data-vv-name="acctPerYr"
                    :data-vv-as="labels.acctPerYr"
                    type="number"
                    v-validate="'required|cannotBeZero'"
                    :hint="getAcctPerYrHint(acctPeriodForm.acctPerYr)"
                    class="warningHint"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6>
                  <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                  <div class="queryValue">{{ acctPeriodForm.createdBy }}</div>
                </v-flex>
                <v-flex xs6>
                  <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                  <div class="queryValue">{{ formatDisplayDate(acctPeriodForm.createdDate) }}</div>
                </v-flex>
                <v-flex xs6 v-if="tranMode !== 'New'">
                  <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                  <div class="queryValue">{{ acctPeriodForm.modifiedBy }}</div>
                </v-flex>
                <v-flex xs6 v-if="tranMode !== 'New'">
                  <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                  <div class="queryValue">{{ formatDisplayDate(acctPeriodForm.modifiedDate) }}</div>
                </v-flex>
              </v-layout>
            </v-container>
          </form>
        </v-card-text>
        <v-flex xs12 row warp>
          <v-btn color="error" dark class=" mr-2" @click.prevent="doCancel">Cancel<v-icon dark right>mdi-cancel</v-icon></v-btn>
          <v-btn v-if="tranMode !== 'Query'" color="primary" type="submit" @click.prevent="doSaveAcctPeriod">
            Save
            <v-icon right>mdi-content-save</v-icon>
          </v-btn>
        </v-flex>
      </v-card>
    </div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { formatDisplayDate } from '@/utils/date'
import { mapGetters } from 'vuex'
import { expandURI } from '@/utils/ajax'
import { removeFormatNumber } from '@/utils/number.js'

export default {
  data() {
    return {
      breadcrumb: [],
      title: 'Accounting Period Review',
      labels: {
        modNo: 'Module',
        acctPerEndDate: 'Accounting Period End Date',
        acctPerMth: 'Accounting Month',
        acctPerYr: 'Accounting Year',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      acctPeriodForm: {
        modNo: '1',
        modNoLbl: '',
        acctPerEndDate: null,
        acctPerMth: 0,
        acctPerYr: 0,
        createdBy: '',
        createdDate: null,
        modifiedBy: '',
        modifiedDate: null,
        actionName: ''
      },
      newAcctPeriod: false,
      acctPerYrWarning: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters(['derieveBreadcrumbByName']),
    menu() {
      return this.$store.state.acctPeriodParam.menu
    },
    acctPerEndDate() {
      return this.$route.params.acctPerEndDate
    },
    modNo() {
      return this.$route.params.modNo
    },
    tranMode() {
      return this.$route.params.tranMode
    },
    modNoList() {
      return [
        { label: 'Underwriting / Reinsurance', value: '1' },
        { label: 'Claim', value: '2' },
        { label: 'Account / General Ledger', value: '3' },
        { label: 'Treaty Accounting', value: '4' }
      ]
    }
  },
  methods: {
    formatDisplayDate(effDate) {
      return formatDisplayDate(effDate)
    },
    doInitForm() {
      this.resetValidator()
      this.resetMessages()
      this.acctPeriodForm.modNo = '1'
      this.acctPeriodForm.modNoLbl = ''
      this.acctPeriodForm.acctPerEndDate = null
      this.acctPeriodForm.acctPerMth = 0
      this.acctPeriodForm.acctPerYr = 0
      this.acctPeriodForm.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
      this.acctPeriodForm.createdDate = new Date()
    },
    async fetchData() {
      this.loading = true
      const apiURI = this.menu.getReview
      const expand = { modNo: this.modNo, acctPerEndDate: this.acctPerEndDate }
      if (apiURI) {
        const result = await this.$http.get(expandURI(apiURI, expand))
        const { data } = result.data
        if (data) {
          let modNoObj = this.modNoList.find((o) => data.acctPeriod.modNo === o.value)
          this.acctPeriodForm.modNo = data.acctPeriod.modNo
          this.acctPeriodForm.modNoLbl = modNoObj === undefined ? '' : modNoObj.label
          this.acctPeriodForm.acctPerEndDate = data.acctPeriod.acctPerEndDate
          this.acctPeriodForm.acctPerMth = data.acctPeriod.acctPerMth
          this.acctPeriodForm.acctPerYr = data.acctPeriod.acctPerYr
          this.acctPeriodForm.createdBy = data.acctPeriod.createdBy
          this.acctPeriodForm.createdDate = data.acctPeriod.createdDate
          this.acctPeriodForm.modifiedBy = data.acctPeriod.modifiedBy
          this.acctPeriodForm.modifiedDate = data.acctPeriod.modifiedDate
        }
      }
      this.loading = false
    },
    doSaveAcctPeriod() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newAcctPeriod) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.performAction
      if (apiURI) {
        this.submitting = true
        try {
          this.acctPeriodForm.actionName = 'CREATE'
          const result = await this.$http.post(apiURI, this.acctPeriodForm)
          const { data } = result.data
          if (data) {
            this.notify(data.acctPeriod)
          }
          this.submitting = false
          this.$router.push({ name: 'acctPeriod' })
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
        this.submitting = false
      }
    },
    async doUpdate() {
      const apiURI = this.menu.performAction
      if (apiURI) {
        this.submitting = true
        try {
          this.acctPeriodForm.actionName = 'UPDATE'
          const result = await this.$http.post(apiURI, this.acctPeriodForm)
          const { data } = result.data
          if (data) {
            this.notify(data.acctPeriod)
          }
          this.submitting = false
          this.$router.push({ name: 'acctPeriod' })
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
        this.submitting = false
      }
    },
    doClose() {
      this.resetValidator()
      this.resetMessages()
      this.acctPeriodForm.modNo = '1'
      this.acctPeriodForm.modNoLbl = ''
      this.acctPeriodForm.acctPerEndDate = null
      this.acctPeriodForm.acctPerMth = 0
      this.acctPeriodForm.acctPerYr = 0
    },
    doCancel() {
      this.$router.push({ name: 'acctPeriod' })
    },
    getAcctPerYrHint(value) {
      this.acctPerYrWarning = false
      if (value) {
        let today = new Date()
        if (removeFormatNumber(value) < today.getFullYear()) {
          this.acctPerYrWarning = true
          return 'Past year, is this correct?'
        }
      }
      return ''
    }
  },
  created() {
    this.breadcrumb = this.derieveBreadcrumbByName('acctPeriod')
    this.breadcrumb.push({
      name: 'acctPeriodDet',
      text: this.title
    })
    if (this.tranMode === 'New') {
      this.newAcctPeriod = true
      this.doInitForm()
    } else {
      this.newAcctPeriod = false
      this.fetchData()
    }
    const acctPerMthRange = {
      getMessage: (field, args) => {
        return field + ' must be > 0 and < 14.'
      },
      validate: (value, args) => {
        return removeFormatNumber(value) > 0 && removeFormatNumber(value) < 14
      }
    }
    const isPastYear = {
      getMessage: (field, args) => {
        return field + ' is a past year.'
      },
      validate: (value, args) => {
        if (value) {
          let today = new Date()
          return value.getFullYear() >= today.getFullYear()
        } else {
          return true
        }
      }
    }
    const cannotBeZero = {
      getMessage: (field, args) => {
        return field + ' cannot be 0.'
      },
      validate: (value, args) => {
        return removeFormatNumber(value) > 0
      }
    }
    this.$validator.extend('cannotBeZero', cannotBeZero)
    this.$validator.extend('acctPerMthRange', acctPerMthRange)
    this.$validator.extend('isPastYear', isPastYear)
  },
  mixins: [MessageMixin]
}
</script>
<style lang="scss">
.warningHint {
  .v-messages__message {
    color: $error !important;
  }
}
</style>
